import { Dispatch } from 'redux';

import { baseApi, defaultBaseApi } from '~src/services/ApiController';
import {
  ADD_USER_FAILED,
  ADD_USER_REQUEST,
  ADD_USER_SUCCESS,
  DELETE_USER_FAILED,
  DELETE_USER_REQUEST,
  DELETE_USER_SUCCESS,
  GET_USER_FAILED,
  GET_USER_REQUEST,
  GET_USER_SUCCESS,
  GET_USERS_FAILED,
  GET_USERS_REQUEST,
  GET_USERS_SUCCESS,
  MAKE_ADMIN_FAILED,
  MAKE_ADMIN_REQUEST,
  MAKE_ADMIN_SUCCESS,
  REVERT_ADMIN_FAILED,
  REVERT_ADMIN_REQUEST,
  REVERT_ADMIN_SUCCESS,
  UPDATE_ATTRIBUTES_FAILED,
  UPDATE_ATTRIBUTES_REQUEST,
  UPDATE_ATTRIBUTES_SUCCESS,
  UPDATE_USER_FAILED,
  UPDATE_USER_REQUEST,
  UPDATE_USER_SUCCESS,
  USER_MANAGEMENT_UPDATE_USER_IMAGE,
  RESET_USERS,
  UPDATE_OWN_ATTRIBUTE,
  INVITE_USER_REQUEST,
  INVITE_USER_SUCCESS,
  INVITE_USER_FAILED,
  MAKE_INVITE_ALL_REQUEST,
  MAKE_INVITE_ALL_SUCCESS,
  MAKE_INVITE_ALL_FAILED,
  CHANGE_ACTIVE_STATUS_USER_REQUEST,
  CHANGE_ACTIVE_STATUS_USER_SUCCESS,
  CHANGE_ACTIVE_STATUS_USER_FAILED,
} from '~src/modules/users/constants/actions';
import { UsersRespI } from '~src/modules/users/interfaces';
import { AsyncAction } from '~src/services/apiMiddleware';
import { ImageResponse } from '~src/shared/imageUploader/sendImageToBackend';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { getUserFullName, User, UserCore, UserCorePatch } from '~src/interfaces/user.interface';
import { SHOW_NOTIFICATION } from '~src/store/app/actionTypes';

export interface FetchUserParams {
  page?: number;
  sort?: SortingValues;
  search?: string;
  per_page?: number;
}

export const fetchUsers = (params: FetchUserParams, loadMore?: boolean): AsyncAction<UsersRespI, {loadMore?: boolean}> => {
  const replacedParams = {
    ...params,
    'filter.user': params.search,
  };
  delete replacedParams.search;
  return {
    types: [GET_USERS_REQUEST, GET_USERS_SUCCESS, GET_USERS_FAILED],
    callAPI: () => defaultBaseApi.getUsers(replacedParams),
    payload: { loadMore },
  };
};

export function fetchUser(uuid: string): AsyncAction<User, {uuid: string}> {
  return {
    types: [GET_USER_REQUEST, GET_USER_SUCCESS, GET_USER_FAILED],
    callAPI: () =>
      defaultBaseApi.getUser(uuid, {setting_attributes: true, custom_attributes: true, manager_attributes: true, permissions: true}),
    payload: { uuid },
  };
}

export function inviteUser(userId: string, section: 'okr' | 'survey' | 'spaceloop') {
  return {
    types: [INVITE_USER_REQUEST, INVITE_USER_SUCCESS, INVITE_USER_FAILED],
    callAPI: () => baseApi.post(`/users/${userId}/invite?section=${section}`),
  };
}

export function addUser(values: UserCore): AsyncAction<User, UserActionPayload> {
  return {
    types: [ADD_USER_REQUEST, ADD_USER_SUCCESS, ADD_USER_FAILED],
    callAPI: () => baseApi.post('/create', values),
    payload: { fullName: getUserFullName(values) },
  };
}

export interface UserActionPayload {
  fullName: string;
}

export function updateUser(
  userId: string,
  values: UserCorePatch,
  fullName: string,
): AsyncAction<User, UserActionPayload> {
  return {
    types: [UPDATE_USER_REQUEST, UPDATE_USER_SUCCESS, UPDATE_USER_FAILED],
    callAPI: () => baseApi.patch(`/users/${userId}`, values),
    payload: { fullName },
  };
}

export function changeUserActiveStatus(
  userId: string,
  deactivated: boolean,
  fullName: string,
): AsyncAction<User, {userId: string, deactivated: boolean, fullName: string}> {
  return {
    types: [
      CHANGE_ACTIVE_STATUS_USER_REQUEST,
      CHANGE_ACTIVE_STATUS_USER_SUCCESS,
      CHANGE_ACTIVE_STATUS_USER_FAILED,
    ],
    callAPI: () => baseApi.patch(`/users/${userId}`, { deactivated }),
    payload: { userId, deactivated, fullName },
  };
}

export const deleteUser = (user: User): AsyncAction<void, UserActionPayload> => ({
  types: [DELETE_USER_REQUEST, DELETE_USER_SUCCESS, DELETE_USER_FAILED],
  callAPI: () => baseApi.delete(`/users/${user.uuid}`),
  payload: { fullName: getUserFullName(user) },
});

export function updateAttributes(userId: string, values: Record<string, any>) {
  return {
    types: [UPDATE_ATTRIBUTES_REQUEST, UPDATE_ATTRIBUTES_SUCCESS, UPDATE_ATTRIBUTES_FAILED],
    callAPI: () => baseApi.post(`/users/${userId}/attributes`, values),
    payload: values,
  };
}

export const changeAdmin = (uuid: string, isAdmin: boolean) =>
  isAdmin ? makeAdmin(uuid) : revertAdmin(uuid);

export const makeAdmin = (uuid: string) => ({
  types: [MAKE_ADMIN_REQUEST, MAKE_ADMIN_SUCCESS, MAKE_ADMIN_FAILED],
  callAPI: () => baseApi.put(`/admins/${uuid}`),
});

export const inviteAll = (section?: string) => ({
  types: [MAKE_INVITE_ALL_REQUEST, MAKE_INVITE_ALL_SUCCESS, MAKE_INVITE_ALL_FAILED],
  callAPI: () => baseApi.post(`/users/invite${section ? '?section=' + section : ''}`),
});

export const revertAdmin = (uuid: string) => ({
  types: [REVERT_ADMIN_REQUEST, REVERT_ADMIN_SUCCESS, REVERT_ADMIN_FAILED],
  callAPI: () => baseApi.delete(`/admins/${uuid}`),
});

export function updateOwnAttribute(key: string, value: string | number | null) {
  return {
    type: UPDATE_OWN_ATTRIBUTE,
    payload: {
      key,
      value,
    },
  };
}

export const userManagementUpdateUserImage = (data: ImageResponse) => (dispatch: Dispatch) => {
  dispatch({
    type: SHOW_NOTIFICATION,
    payload: { txtKey: 'x.profile_settings.image_updated_message' },
  });
  return dispatch({ type: USER_MANAGEMENT_UPDATE_USER_IMAGE, data });
};

export function resetUsers() {
  return { type: RESET_USERS };
}
